"use client";

import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import "./Feed.css";

import PostComposer from "../../components/HomePage/PostComposer/PostComposer";
import PostCard from "../../components/HomePage/PostCard/PostCard";
import { fetchDoctors, getCurrentDoctor } from "../../../authDoctor/redux/thunks/doctorsThunks";
import InviteFriendsSection from "../../components/HomePage/InviteFriendsSection/InviteFriendsSection";
import AdBanner from "../../components/HomePage/AdBanner/AdBanner";
import MedilinkInfoCard from "../../components/FeedPage/MedilinkInfoCard/MedilinkInfoCard";

import usePostsDoctor from "../../hooks/usePostsDoctor";
import useSavedItems from "../../../authorization/useSavedItems";

/* ---------- time ago ---------- */
const timeAgoLabel = (date) => {
  if (!date) return "";
  const secs = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  const map = [
    ["y", 31536000],
    ["mo", 2592000],
    ["w", 604800],
    ["d", 86400],
    ["h", 3600],
    ["m", 60],
  ];
  for (const [k, v] of map) {
    const n = Math.floor(secs / v);
    if (n >= 1) return `${n}${k}`;
  }
  return "now";
};

/* ---------- helpers ---------- */
const s = (v) => (v == null ? "" : String(v)).trim();

const get = (obj, path) => {
  if (!obj || !path) return undefined;
  const parts = String(path).split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

const firstNonEmpty = (arr) => {
  for (const v of arr) {
    const str = s(v);
    if (str) return str;
  }
  return "";
};

const buildNameFromObj = (obj) =>
  firstNonEmpty([
    obj?.fullName,
    obj?.name,
    obj?.displayName,
    obj?.username,
    `${s(obj?.firstName || obj?.firstname)} ${s(obj?.lastName || obj?.lastname)}`.trim(),
  ]);

const FeedDoctor = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  /* ================== current doctor (lecture seulement) ================== */
  const doctorSlice = useSelector((state) => state.doctor || {});
  const currentUser = doctorSlice?.user || null;
  const userLoading = doctorSlice?.loading ?? false;
  const userError = doctorSlice?.error ?? null;

  useEffect(() => {
    if (!currentUser) {
      dispatch(getCurrentDoctor());
    }
  }, [dispatch, currentUser]);

  // ✅ ID Postgres (integer) pour comments/likes (actor)
  const doctorPgId =
    currentUser?.pgId ??
    currentUser?.userId ??
    currentUser?.id ??
    null;

  // ✅ ID pour posts-service (celui que ton backend attend dans /posts/create/:userId)
  const doctorId = currentUser?.id || currentUser?.userId || currentUser?._id || currentUser?.pgId || null;

  /* ================== doctors list ================== */
  const doctorsList = doctorSlice?.doctors || [];

  useEffect(() => {
    dispatch(fetchDoctors());
  }, [dispatch]);

  const doctorsByAnyId = useMemo(() => {
    const map = {};
    (Array.isArray(doctorsList) ? doctorsList : []).forEach((d) => {
      const ids = [
        d?.id,
        d?._id,
        d?.userId,
        d?.pgId,
        d?.raw?.id,
        d?.raw?._id,
        d?.raw?.userId,
        d?.raw?.pgId,
        d?.user?.id,
        d?.user?._id,
        d?.user?.pgId,
      ]
        .map((x) => (x == null ? "" : String(x)))
        .filter(Boolean);

      ids.forEach((k) => (map[k] = d));
    });
    return map;
  }, [doctorsList]);

  const doctorsByPgId = useMemo(() => {
    const map = {};
    (Array.isArray(doctorsList) ? doctorsList : []).forEach((d) => {
      const pg = d?.pgId ?? d?.userId ?? (Number.isFinite(Number(d?.id)) ? Number(d.id) : null);
      if (pg != null) map[String(pg)] = d;
    });
    return map;
  }, [doctorsList]);

  /* ================== posts hook ================== */
  const {
    posts,
    status,
    hasMore,
    reload,
    uploadFiles,
    isUploading,
    createPost,
    updatePost,
    deletePost,
  } = usePostsDoctor(doctorId, {
    autoFetch: true,
    pageSize: 20,
    mode: "feed",
  });
  const { isSaved, toggleSave, pendingKey } = useSavedItems({ enabled: !!currentUser });

  const [currentPage, setCurrentPage] = useState(1);
  const [isPublishing, setIsPublishing] = useState(false);
  const [updatingPostId, setUpdatingPostId] = useState(null);
  const [deletingPostId, setDeletingPostId] = useState(null);

  // ✅ FIX: store comments counts per post, fetched on mount
  const [commentsCounts, setCommentsCounts] = useState({});

  // ✅ FIX: when posts load, fetch comment counts for all posts immediately
  // so the count is visible without clicking "Commenter"
  useEffect(() => {
    if (!Array.isArray(posts) || posts.length === 0) return;

    posts.forEach((post) => {
      const pid = post?.postId || post?._id || post?.id;
      if (!pid) return;

      // If the backend already sends the count in the post object, use it directly
      const backendCount =
        post?.commentsCount ??
        post?.comments_count ??
        post?.commentCount ??
        post?.commentsTotal ??
        post?.nbComments ??
        (Array.isArray(post?.comments) ? post.comments.length : undefined);

      if (backendCount != null) {
        setCommentsCounts((prev) => ({
          ...prev,
          [pid]: backendCount,
        }));
      }
    });
  }, [posts]);

  const handleLoadMore = () => {
    if (!hasMore || status === "loading") return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    reload(nextPage, 20);
  };

  /* ================== author resolver ================== */
  const getPostAuthorId = useCallback((post) => {
    return firstNonEmpty([
      post?.authorId,
      post?.doctorId,
      post?.userId,
      get(post, "author.id"),
      get(post, "author._id"),
      get(post, "user.id"),
      get(post, "user._id"),
      get(post, "doctor.id"),
      get(post, "doctor._id"),
      get(post, "createdBy.id"),
      get(post, "createdBy._id"),
      get(post, "postedBy.id"),
      get(post, "postedBy._id"),
    ]);
  }, []);

  const getPostAuthorName = useCallback(
    (post) => {
      const direct = firstNonEmpty([
        post?.authorName,
        post?.userName,
        post?.doctorName,
        post?.createdByName,
        post?.postedByName,
        post?.ownerName,

        buildNameFromObj(post?.author),
        buildNameFromObj(post?.user),
        buildNameFromObj(post?.doctor),
        buildNameFromObj(get(post, "createdBy")),
        buildNameFromObj(get(post, "postedBy")),
      ]);
      if (direct) return direct;

      const uid = s(getPostAuthorId(post));
      const found = uid ? doctorsByAnyId[uid] : null;
      const fromList = buildNameFromObj(found) || buildNameFromObj(found?.raw);
      if (fromList) return fromList;

      return "Médecin inconnu";
    },
    [doctorsByAnyId, t, getPostAuthorId]
  );

  const getPostAuthorAvatar = useCallback(
    (post) => {
      const direct =
        post?.author?.avatar ||
        post?.author?.profileImage ||
        post?.user?.avatar ||
        post?.user?.profileImage ||
        post?.doctor?.avatar ||
        post?.doctor?.profileImage ||
        get(post, "createdBy.avatar") ||
        get(post, "postedBy.avatar") ||
        null;

      if (direct) return direct;

      const uid = s(getPostAuthorId(post));
      const found = uid ? doctorsByAnyId[uid] : null;
      return found?.avatar || found?.profileImage || found?.raw?.avatar || null;
    },
    [doctorsByAnyId, getPostAuthorId]
  );

  /* ================== actions ================== */
  const handlePublish = async (payload) => {
    if (!doctorId) {
      alert(t("doctorFeed.publishUserMissing"));
      return;
    }
    setIsPublishing(true);
    try {
      const res = await createPost({ userId: doctorId, payload });
      if (res?.type?.includes("fulfilled")) {
        setCurrentPage(1);
        reload(1, 20);
        return res.payload;
      }
      throw new Error(typeof res?.payload === 'string' ? res.payload : res?.payload?.message || t('doctorFeed.publishError'));
    } catch (err) {
      console.error("❌ Erreur publish:", err);
      throw err;
    } finally {
      setIsPublishing(false);
    }
  };

  // ✅ UPDATE: on passe userId (doctorId) obligatoire pour /update/by-post-id/:postId/:userId
  const handleEditPost = async (postId, data) => {
    if (!postId) return;
    if (!doctorId) return;

    setUpdatingPostId(postId);
    try {
      let payload = data;
      if (typeof data === "string") {
        payload = { content: data };
      } else if (data?.description !== undefined && data?.content === undefined) {
        const { description, ...rest } = data;
        payload = { ...rest, content: description };
      }
      const res = await updatePost(postId, doctorId, payload);
      if (res?.type?.includes("fulfilled")) reload(currentPage, 20);
      else throw new Error(typeof res?.payload === 'string' ? res.payload : res?.payload?.message || t('doctorFeed.updateError'));
    } catch (err) {
      console.error("❌ Erreur update:", err);
      alert(`❌ ${t("doctorFeed.updateError")} ${err?.message || t("doctorFeed.unknownError")}`);
    } finally {
      setUpdatingPostId(null);
    }
  };

  // ✅ DELETE: par postId
  const handleDeletePost = async (postId) => {
    if (!postId) return;

    setDeletingPostId(postId);
    try {
      const res = await deletePost(postId, doctorId);
      if (res?.type?.includes("fulfilled")) {
        setCurrentPage(1);
        reload(1, 20);
      } else {
        throw new Error(res?.payload || "Delete failed");
      }
    } catch (err) {
      console.error("❌ Erreur delete:", err);
      alert(`❌ ${t("doctorFeed.deleteError")} ${err?.message || t("doctorFeed.unknownError")}`);
    } finally {
      setDeletingPostId(null);
    }
  };

  /* ================== guards ================== */
  if (!currentUser && userLoading) {
    return (
      <AppLayoutDoctor>
        <div className="page-content">
          <div className="feed-loading">
            <div className="loading-spinner"></div>
            <p>{t("doctorFeed.loadingProfile")}</p>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  if (!currentUser && userError) {
    return (
      <AppLayoutDoctor>
        <div className="page-content">
          <div className="feed-error-banner">
            <p>
              ❌ {t("doctorFeed.loadProfileError")} {userError}
            </p>
            <button onClick={() => window.location.reload()} className="retry-btn">
              {t("doctorFeed.retry")}
            </button>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  if (!currentUser) {
    return (
      <AppLayoutDoctor>
        <div className="page-content">
          <div className="feed-error-banner">
            <p>⚠️ Session expirée. Veuillez vous reconnecter.</p>
            <button onClick={() => (window.location.href = "/login")} className="retry-btn">
              {t("doctorFeed.signIn")}
            </button>
          </div>
        </div>
      </AppLayoutDoctor>
    );
  }

  /* ================== render ================== */
  return (
    <AppLayoutDoctor>
      <div className="page-content">
        <div className="feed-container">
          <div className="main-content">
            <PostComposer
              onPublish={handlePublish}
              isPublishing={isPublishing}
              uploadFiles={uploadFiles}
              isUploading={isUploading}
            />

            {status === "loading" && posts.length === 0 && (
              <div className="feed-loading">
                <div className="loading-spinner"></div>
                <p>{t("doctorFeed.postsLoading")}</p>
              </div>
            )}

            <div className="posts-section">
              {posts?.length > 0 ? (
                <>
                  {posts.map((post) => {
                    const pid = post?.postId || post?._id || post?.id || `${Math.random()}`;

                    // ✅ FIX: use cached count (populated on mount) OR fallback to post fields
                    const commentsCountForPost =
                      commentsCounts[pid] ??
                      post?.commentsCount ??
                      post?.comments_count ??
                      post?.commentCount ??
                      post?.commentsTotal ??
                      post?.nbComments ??
                      (Array.isArray(post?.comments) ? post.comments.length : undefined) ??
                      0;

                    return (
                      <div key={pid} className="post-item">
                        <PostCard
                          postId={pid}
                          doctorName={getPostAuthorName(post)}
                          timeAgo={timeAgoLabel(post?.createdAt)}
                          content={post?.content || post?.text}
                          image={post?.imageUrl}
                          attachments={post?.attachments || []}
                          likes={post?.likes || 0}
                          comments={commentsCountForPost}
                          profileImage={getPostAuthorAvatar(post)}

                          // ✅ NEW
                          visibleToDoctorsOnly={!!post?.visibleToDoctorsOnly}

                          onEdit={handleEditPost}
                          onDelete={handleDeletePost}
                          canManagePost={String(post?.userId ?? "") === String(doctorId ?? "")}
                          isUpdating={updatingPostId === post?.postId}
                          isDeleting={deletingPostId === post?.postId}
                          currentUserId={doctorPgId}
                          doctorsByPgId={doctorsByPgId}
                          postOwnerId={getPostAuthorId(post) || null}
                          isSaved={isSaved("post", pid)}
                          onToggleSave={(postId) => toggleSave("post", postId)}
                          isSaving={pendingKey === `post:${String(pid)}`}
                          createdAt={post?.createdAt}
                        />
                      </div>
                    );
                  })}

                  {hasMore && (
                    <div className="load-more-section">
                      <button className="load-more-btn" onClick={handleLoadMore} disabled={status === "loading"}>
                        {status === "loading" ? (
                          <>
                            <span className="spinner-small"></span>
                            {t("doctorFeed.loadingMore")}
                          </>
                        ) : (
                          "Charger plus"
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                status !== "loading" && (
                  <div className="empty-feed">
                    <div className="empty-icon">📭</div>
                    <h3>{t("doctorFeed.empty.title")}</h3>
                    <p>{t("doctorFeed.empty.subtitle")}</p>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="sidebar-doctor">
            {/* <AdBanner /> */}
            <InviteFriendsSection />
            <MedilinkInfoCard />
          </div>
        </div>
      </div>
    </AppLayoutDoctor>
  );
};

export default FeedDoctor;
